import React, { lazy } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';

const UserHome = lazy(() => import('../../pages/UserDashboard/UserHome'));
const LandingVariantA = lazy(() => import('../../pages/Landing/LandingVariantA'));
const LandingVariantB = lazy(() => import('../../pages/Landing/LandingVariantB'));
import { getCurrentRole, getDashboardPath } from '../../utils/sessionManager';

/**
 * Landing Page Controller
 * Controls which landing page variant to display based on URL parameters
 */
const LandingPageController = () => {
    // Check for role synchronously during render to avoid content flash
    const currentRole = getCurrentRole();

    React.useEffect(() => {
        if (!currentRole) {
            // Automatically enable guest mode for visitors landing on the portal
            localStorage.setItem('guestMode', 'true');
        }
    }, [currentRole]);

    // If authenticated, redirect immediately without waiting or showing a stuck loader
    if (currentRole) {
        const dashboardPath = getDashboardPath(currentRole);
        return <Navigate to={dashboardPath} replace />;
    }

    const [searchParams] = useSearchParams();
    const pageParam = searchParams.get('page');

    // Determine which landing page to show
    const getLandingPage = () => {
        // Check URL parameter first (highest priority)
        if (pageParam) {
            const normalizedParam = pageParam.toLowerCase().trim();

            // Clear preference if explicitly set to default
            if (normalizedParam === 'default') {
                localStorage.removeItem('preferredLanding');
                return 'default';
            }

            // Store preference for future visits
            localStorage.setItem('preferredLanding', normalizedParam);
            return normalizedParam;
        }

        // Check localStorage for persistent preference
        const stored = localStorage.getItem('preferredLanding');
        if (stored) {
            return stored;
        }

        // Default landing page
        return 'default';
    };

    const landingPage = getLandingPage();

    // Log for debugging (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
        console.log(`[LandingPageController] Rendering landing page: ${landingPage}`);
    }

    // Render appropriate landing page component
    switch (landingPage) {
        case 'a':
        case 'variant-a':
            return <LandingVariantA />;

        case 'b':
        case 'variant-b':
            return <LandingVariantB />;

        case 'default':
        default:
            return <UserHome />;
    }
};

export default LandingPageController;
